// The open-deal resync App schedules after a brokerage switch or an in-place
// plan edit (final review F4), bound to the workspace it was scheduled for.
//
// usePendingBrokerageResync(selectedWorkspaceId) holds { workspaceId,
// brokerage }. Its `brokerage` is the pending brokerage only while that
// workspace is selected; App opens the deals and team-split leases on it. A
// change of workspace drops the pending resync outright: running it later on
// another workspace would write the first workspace's brokerage onto that
// workspace's open deals (an eXp-only one included), and coming back is not a
// reason to revive it (the next deal save resyncs).
//
// useRunPendingBrokerageResync runs it once, when `ready` (the selected
// workspace's leases have loaded).

import React from 'react';

export function usePendingBrokerageResync(selectedWorkspaceId) {
    const [pending, setPending] = React.useState(null);
    const schedule = React.useCallback((workspaceId, brokerage) => {
        if (workspaceId && brokerage?.current) setPending({ workspaceId, brokerage });
    }, []);
    const clear = React.useCallback(() => setPending(null), []);

    React.useEffect(() => {
        if (pending && pending.workspaceId !== selectedWorkspaceId) setPending(null);
    }, [pending, selectedWorkspaceId]);

    const brokerage = pending && pending.workspaceId === selectedWorkspaceId ? pending.brokerage : null;
    return { brokerage, workspaceId: brokerage ? pending.workspaceId : null, schedule, clear };
}

export function useRunPendingBrokerageResync({ pending, ready, resyncOpenDeals }) {
    React.useEffect(() => {
        if (!pending.brokerage || !ready) return;
        const { brokerage } = pending;
        pending.clear();
        resyncOpenDeals(brokerage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending.brokerage, ready]);
}
